import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";

import * as TanStackQueryProvider from "./integrations/tanstack-query/root-provider.tsx";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";

import "./styles.css";
import { env } from "./config/env";

// Initialize Sentry before anything else
import { initializeSentry, Sentry } from "./config/sentry";
import reportWebVitals from "./reportWebVitals.ts";

initializeSentry();

import { i18n } from "@lingui/core";
import { I18nProvider } from "@lingui/react";
import { PostHogProvider } from "posthog-js/react";

import { initializeI18n } from "./lib/i18n-loader";
// Import our language detection and loading utilities
import { detectLanguageFromUrl } from "./lib/language-detection";

// Create a new router instance
const TanStackQueryProviderContext = TanStackQueryProvider.getContext();

// Create the router with hot reload support
const router = createRouter({
	routeTree,
	context: {
		...TanStackQueryProviderContext,
	},
	defaultPreload: "intent",
	scrollRestoration: true,
	defaultStructuralSharing: true,
	defaultPreloadStaleTime: 0,
});

// Handle hot reload in development
if (import.meta.hot) {
	// Accept updates to the route tree
	import.meta.hot.accept("./routeTree.gen", (newRouteTree) => {
		if (newRouteTree) {
			// Update the router with new route tree
			router.update({
				routeTree: newRouteTree.routeTree,
				context: {
					...TanStackQueryProviderContext,
				},
			});
		}
	});

	// Dispose of the router when hot reloading
	import.meta.hot.dispose(() => {
		router.cancelMatches();
	});
}

// Register the router instance for type safety
declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}

/**
 * Initialize and render the app with dynamic language loading
 */
async function initializeApp() {
	try {
		// Detect language from URL parameters
		const detectedLanguage = detectLanguageFromUrl();

		// Initialize i18n with the detected language
		await initializeI18n(detectedLanguage, true);

		// Render the app after language is loaded
		const rootElement = document.getElementById("app");
		if (rootElement && !rootElement.innerHTML) {
			const root = ReactDOM.createRoot(rootElement);
			root.render(
				<Sentry.ErrorBoundary
					fallback={({ error, resetError }) => (
						<div style={{ padding: "20px", textAlign: "center" }}>
							<h2>Something went wrong</h2>
							<p>
								{error instanceof Error
									? error.message
									: "An unknown error occurred"}
							</p>
							<button onClick={resetError} type="button">
								Try again
							</button>
						</div>
					)}
					showDialog
				>
					<PostHogProvider
						apiKey={env.POSTHOG_API_KEY}
						options={{
							api_host: env.POSTHOG_API_HOST,
							capture_exceptions: false, // Disabled to prevent interference with API error handling
							debug: false, // Disabled to prevent network interference
							disable_session_recording: false,
							loaded: () => {
								// Only capture events, not network requests or exceptions
							},
						}}
					>
						<I18nProvider i18n={i18n}>
							<TanStackQueryProvider.Provider {...TanStackQueryProviderContext}>
								<RouterProvider router={router} />
							</TanStackQueryProvider.Provider>
						</I18nProvider>
					</PostHogProvider>
				</Sentry.ErrorBoundary>,
			);
		}
	} catch (error) {
		console.error("Failed to initialize app:", error);

		// Fallback: render app with default English
		const rootElement = document.getElementById("app");
		if (rootElement) {
			const root = ReactDOM.createRoot(rootElement);
			root.render(
				<StrictMode>
					<div
						style={{
							padding: "20px",
							textAlign: "center",
							color: "red",
							fontFamily: "Arial, sans-serif",
						}}
					>
						<h2>Language Loading Error</h2>
						<p>Failed to load language resources. Please refresh the page.</p>
						<p>
							Error: {error instanceof Error ? error.message : "Unknown error"}
						</p>
					</div>
				</StrictMode>,
			);
		}
	}
}

// Initialize the app
initializeApp();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
